// Unit Master RA images are served from the Lease Finance backend
// (leasefinanceapi.apps.pro360erp.com/Resources/...), not the main
// REACT_APP_IMAGE_URL host used elsewhere in this app. Rows saved before the
// backend's UploadSettingsRA config was actually wired up also carry a
// fullUrl baked against http://localhost:5000 (that class's hardcoded
// default, never bound to real config) - this rewrites either case to the
// real live host so already-saved rows work without needing a DB fix.
const LEASE_FINANCE_IMAGE_URL = (process.env.REACT_APP_BACKEND_URL_LeaseFinance || '').replace(/\/+$/, '');
const LOCALHOST_ORIGIN_PATTERN = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?/i;

// Saved rows can carry the host they were uploaded through (localhost, an older environment, http instead of https);
// the files themselves are always served from the Lease Finance host under /Resources/, so that origin is used for all of them.
const RESOURCES_PATTERN = /^(https?:)?\/\/[^/]+(\/(?:.*\/)?Resources\/.*)$/i;

export const rewriteUnitRAImageUrl = (url) => {
  if (!url || !LEASE_FINANCE_IMAGE_URL) return url;
  try {
    const targetOrigin = new URL(LEASE_FINANCE_IMAGE_URL).origin;
    const match = String(url).match(RESOURCES_PATTERN);
    if (match) return targetOrigin + match[2];
    if (LOCALHOST_ORIGIN_PATTERN.test(url)) return url.replace(LOCALHOST_ORIGIN_PATTERN, targetOrigin);
    // relative path such as "Resources/Images/a.jpg"
    if (!/^([a-z][a-z0-9+.-]*:|\/\/)/i.test(url)) return targetOrigin + "/" + String(url).replace(/^\/+/, "");
    return url;
  } catch {
    return url;
  }
};

// Uploads the files one request each, in order. A single multi-file request only returned a result for the first file on
// the live server, so every file after it was never saved. Returns the upload results (originalFileName always set) and the
// names of the files that failed, so the caller can tell the user instead of silently dropping them.
export const uploadUnitFiles = async ({ axios, url, files, refNo, token, db }) => {
  const uploaded = [];
  const failed = [];
  for (const file of files) {
    try {
      const formData = new FormData();
      formData.append('files', file);
      formData.append('refNo', refNo || '');
      formData.append('fileCategory', 'Images');
      const response = await axios.post(url, formData, {
        headers: {
          accept: 'text/plain',
          apikey: 'V+kBloEwqqVho1PINmQI0VJQwSBX2l/e',
          Authorization: `Bearer ${token}`,
          db,
        },
      });
      const results = response?.data?.result;
      const list = Array.isArray(results) ? results : results ? [results] : [];
      const hit = list.find((r) => r?.fileName) || list[0];
      if (hit?.fileName) uploaded.push({ ...hit, originalFileName: hit.originalFileName || file.name });
      else failed.push(file.name);
    } catch (err) {
      console.error('Unit file upload failed:', file.name, err);
      failed.push(file.name);
    }
  }
  return { uploaded, failed };
};

// Addresses to try for a saved file, best first: the rewritten one, the address as saved, and the same path on the Lease Finance host.
export const unitMediaUrls = (url) => {
  if (!url) return [];
  const list = [rewriteUnitRAImageUrl(url), url];
  try {
    if (LEASE_FINANCE_IMAGE_URL && /^https?:\/\//i.test(url)) list.push(new URL(LEASE_FINANCE_IMAGE_URL).origin + new URL(url).pathname);
  } catch { /* keep the others */ }
  return [...new Set(list.filter(Boolean))];
};

// The server keeps one image per remark (a blank remark is stored as "0", so the second blank one was silently dropped, and so
// was any repeated remark). So every new file needs a remark and no two files may share one. Returns an error text, or '' when fine.
export const validateImageRemarks = (attachments) => {
  const rows = (attachments || []).filter((row) => row?.fileName);
  const blank = rows.find((row) => row.isNew && row.file && !String(row.remarks || '').trim());
  if (blank) return `Enter remarks for the file "${blank.fileName}". Remarks are required for every file.`;
  const seen = new Map();
  for (const row of rows) {
    const key = String(row.remarks || '').trim().toLowerCase();
    if (!key || key === '0') continue;
    if (seen.has(key)) return `Remarks must be different for each file - "${String(row.remarks).trim()}" is used for "${seen.get(key)}" and "${row.fileName}".`;
    seen.set(key, row.fileName);
  }
  return '';
};
